import { LoadingIndicator } from 'components/loading'
import { useConfiguracoesQuery } from 'graphql/hooks.generated'
import { ConfiguracoesQuery, FrontendLogLevelEnum } from 'graphql/types.generated'
import React, { useCallback, useContext, useMemo } from 'react'

export type ConfiguracoesModel = Pick<
  ConfiguracoesQuery['info'],
  | 'smtpConfigurado'
  | 'internetHabilitada'
  | 'govBREnabled'
  | 'versao'
  | 'buscaCidadaoPorPropriedadesEnabled'
  | 'oldPecConnected'
  | 'firebaseEnabled'
  | 'ativado'
  | 'treinamento'
  | 'isPrescricaoDigitalEnabled'
  | 'dataAtivacaoAssinaturaEletronica'
  | 'rocksdbEnabled'
  | 'tipoInstalacao'
  | 'cadastroDomiciliarViaCdsEnabled'
  | 'cadastroIndividualViaCdsEnabled'
  | 'anexoArquivosEnabled'
  | 'sentry'
  | 'frontendLogLevel'
  | 'linkInstalacao'
>

interface ConfiguracoesContextModel {
  configuracoes: ConfiguracoesModel
  update(): Promise<ConfiguracoesModel>
}

interface ConfiguracoesContextProps {
  children: React.ReactNode
}

const configuracoesFallback: ConfiguracoesModel = {
  smtpConfigurado: false,
  govBREnabled: false,
  internetHabilitada: false,
  versao: null,
  buscaCidadaoPorPropriedadesEnabled: false,
  cadastroDomiciliarViaCdsEnabled: false,
  cadastroIndividualViaCdsEnabled: false,
  oldPecConnected: false,
  firebaseEnabled: false,
  ativado: false,
  treinamento: false,
  isPrescricaoDigitalEnabled: false,
  dataAtivacaoAssinaturaEletronica: null,
  rocksdbEnabled: false,
  tipoInstalacao: null,
  anexoArquivosEnabled: false,
  sentry: { id: '1', enabled: false },
  frontendLogLevel: FrontendLogLevelEnum.OFF,
  linkInstalacao: null,
}

const ConfiguracoesContext = React.createContext<ConfiguracoesContextModel>({
  configuracoes: configuracoesFallback,
  update: () => Promise.resolve(configuracoesFallback),
})

export function ConfiguracoesProvider(props: ConfiguracoesContextProps) {
  const { children } = props
  const { data, loading, refetch } = useConfiguracoesQuery({
    fetchPolicy: 'cache-first',
  })

  const update = useCallback(() => refetch().then((result) => result?.data?.info), [refetch])

  const configuracoes = data?.info
  const value = useMemo(
    () => ({
      configuracoes,
      update,
    }),
    [configuracoes, update]
  )

  return (
    <ConfiguracoesContext.Provider value={value}>
      {loading || !configuracoes ? <LoadingIndicator /> : children}
    </ConfiguracoesContext.Provider>
  )
}

export const useConfiguracoes = () => useContext(ConfiguracoesContext)
